import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { FileText, Download, Loader2 } from 'lucide-react';
import EventNoticeContentForm from './EventNoticeContentForm';

export function EventNoticeDialog({ open, onOpenChange, competition }) {
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [greeting, setGreeting] = useState('Kia ora koutou');
  const [includeFaqs, setIncludeFaqs] = useState(true);
  const [letterheadType, setLetterheadType] = useState('none');

  // Load saved settings whenever the dialog opens / competition changes.
  useEffect(() => {
    if (!open || !competition?.competition_id) return;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(
          `${API}/competitions/${competition.competition_id}/event-notice-settings`,
          { withCredentials: true }
        );
        setGreeting(res.data.greeting || 'Kia ora koutou');
        setIncludeFaqs(!!res.data.include_faqs);
        setLetterheadType(res.data.letterhead_type || 'none');
      } catch {
        setGreeting('Kia ora koutou');
        setIncludeFaqs(true);
        setLetterheadType(competition?.letterhead_type || 'none');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, competition?.competition_id]);

  const persistSettings = async () => {
    if (!competition?.competition_id) return;
    try {
      await axios.put(
        `${API}/competitions/${competition.competition_id}/event-notice-settings`,
        { greeting, include_faqs: includeFaqs },
        { withCredentials: true }
      );
    } catch (err) {
      console.warn('Could not persist Event Notice settings:', err);
    }
  };

  const handleGenerate = async () => {
    if (!greeting.trim()) {
      toast.error('Please enter a greeting');
      return;
    }
    setGenerating(true);
    try {
      // Persist first so the backend reads the latest values and every
      // other caller (Entry Pack attachment, PDF) stays in sync.
      await persistSettings();
      const response = await axios.post(
        `${API}/documents/schedule-covering-letter?competition_id=${competition.competition_id}`,
        {},
        { withCredentials: true, responseType: 'blob' }
      );
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Event_Notice.docx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Event Notice generated and downloaded!');
      onOpenChange(false);
    } catch (error) {
      let errorMessage = 'Failed to generate Event Notice';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          errorMessage = json.detail || errorMessage;
        } catch {}
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      toast.error(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  if (!competition) return null;

  const hasFigures = competition.figure_events_included;
  const hasArtistic = competition.artistic_events_included;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="event-notice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#2D4A3E]">
            <FileText className="h-5 w-5" />
            Generate Event Notice
          </DialogTitle>
          <DialogDescription>
            Configure and generate the Event Notice document for {competition.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          <Card className="p-4 bg-stone-50">
            <h4 className="font-semibold text-[#2D4A3E] mb-2">Competition Details</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-stone-500">Name:</span>
                <span className="ml-2 font-medium">{competition.name}</span>
              </div>
              {competition.sub_competition_name && (
                <div>
                  <span className="text-stone-500">Sub-comp:</span>
                  <span className="ml-2">{competition.sub_competition_name}</span>
                </div>
              )}
              <div>
                <span className="text-stone-500">Type:</span>
                <span className="ml-2">{competition.competition_type}</span>
              </div>
              <div>
                <span className="text-stone-500">Disciplines:</span>
                <span className="ml-2">
                  {[hasArtistic && 'Artistic', hasFigures && 'Figures'].filter(Boolean).join(', ') || 'None selected'}
                </span>
              </div>
            </div>
          </Card>

          {loading ? (
            <div className="p-6 text-center text-sm text-stone-500">Loading saved settings…</div>
          ) : (
            <EventNoticeContentForm
              competitionId={competition.competition_id}
              greeting={greeting}
              onGreetingChange={setGreeting}
              includeFaqs={includeFaqs}
              onIncludeFaqsChange={setIncludeFaqs}
              letterheadType={letterheadType}
              onLetterheadTypeChange={setLetterheadType}
              testIdPrefix="en"
            />
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="cancel-event-notice-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={generating || loading}
              className="btn-primary"
              data-testid="generate-event-notice-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Save &amp; Generate Event Notice
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
